<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        no-close-on-backdrop
        no-close-on-esc
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Parking Fee</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Parking Fee *"
                        label-for="name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.name"
                            type="text"
                            id="name"
                            placeholder="Enter Parking Fee"
                            :class="{
                                'is-invalid':
                                    vParking$.form.name.$dirty &&
                                    vParking$.form.name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                vParking$.form.name.$dirty && vParking$.form.name.$invalid
                            "
                        >
                            {{ vParking$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Amount *"
                        label-for="amount"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.amount"
                            type="number"
                            id="amount"
                            placeholder="0"
                            :class="{
                                'is-invalid':
                                    vParking$.form.amount.$dirty &&
                                    vParking$.form.amount.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                vParking$.form.amount.$dirty && vParking$.form.amount.$invalid
                            "
                        >
                            {{ vParking$.form.amount.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Description"
                        label-for="description"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.description"
                            id="description"
                            placeholder="Optional note"
                            rows="3"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormTextarea,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useToast } from "vue-toastification";

const toast = useToast();

// Props
const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
});

// Emit
const emit = defineEmits(["update:modal", "addOrUpdate"]);

// Function to update modal visibility
const updateModal = (value) => {
    emit("update:modal", value); // Sync modal state with parent
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    vParking$.value.$reset();
};

const formData = {
    name: "",
    amount: "",
    description: "",
};

const form = reactive({ ...formData });

const rules = {
    form: {
        name: {
            required: helpers.withMessage("Parking Location is required", required),
        },
        amount: {
            required: helpers.withMessage("Amount is required", required),
            minValue: helpers.withMessage(
                "Amount must be at least 1",
                minValue(1)
            ),
        },
    },
};

const vParking$ = useVuelidate(rules, { form });

// Init data jika update
watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
        } else {
            Object.assign(form, { ...formData });
        }
        vParking$.value.$reset();
    },
    { immediate: true }
);

const handleAction = () => {
    props.selectedData && props.selectedData.index !== undefined ? update() : create();
};

const create = async () => {
    vParking$.value.$touch();
    if (!vParking$.value.$invalid) {
        try {
            const payload = { ...form };
            delete payload.index;
            emit("addOrUpdate", payload);
            toast.success("Parking fee added successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to add parking fee");
        }
    }
};

const update = async () => {
    vParking$.value.$touch();
    if (!vParking$.value.$invalid) {
        try {
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...form,
            });
            toast.success("Parking fee updated successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to update parking fee");
        }
    }
};
</script>
